import { useQuery } from '@tanstack/react-query';
import {
  csvExportPeriods,
  type CsvExportSummary,
  formatMoney,
  italianToday,
} from '@k-management/shared';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Field } from '../../components/Field.js';
import { apiDownload, apiRequest } from '../../lib/api.js';

/**
 * «Esporta CSV» of the bank movements and of the card spending, for the
 * accountant (2.30): a period, «Mese scorso» to start with, the count and
 * the totals of the file before downloading it.
 */
export function CsvExportDialog({
  title,
  subtitle,
  path,
  extra = {},
  fields,
  note,
  fallbackName,
  onClose,
}: {
  title: string;
  subtitle?: string;
  /** The API path of the file; `/summary` after it gives the counts. */
  path: string;
  /** More of the query, such as the card. */
  extra?: Record<string, string>;
  fields?: ReactNode;
  note?: ReactNode;
  fallbackName: string;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const periods = csvExportPeriods(italianToday());
  const [period, setPeriod] = useState({ from: periods[0]!.from, to: periods[0]!.to });
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const valid = Boolean(period.from && period.to && period.from <= period.to);
  const search = new URLSearchParams({ ...period, ...extra }).toString();
  const summary = useQuery({
    queryKey: ['treasury', 'csv-summary', path, search],
    queryFn: () => apiRequest<CsvExportSummary>(`${path}/summary?${search}`),
    enabled: valid,
  });
  const download = async () => {
    setError(null);
    setDownloading(true);
    try {
      await apiDownload(`${path}?${search}`, fallbackName);
      onClose();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    } finally {
      setDownloading(false);
    }
  };
  return (
    <dialog ref={dialogRef} className="dialog narrow" onClose={onClose}>
      <div className="stack">
        <div>
          <h2>{title}</h2>
          {subtitle && <p className="muted small">{subtitle}</p>}
        </div>
        <div className="row">
          <Field label="Dal">
            <input
              type="date"
              value={period.from}
              onChange={(event) => setPeriod({ ...period, from: event.target.value })}
            />
          </Field>
          <Field label="Al">
            <input
              type="date"
              value={period.to}
              onChange={(event) => setPeriod({ ...period, to: event.target.value })}
            />
          </Field>
          {fields}
        </div>
        <div className="row">
          {periods.map((item) => (
            <button
              key={item.label}
              type="button"
              className={
                item.from === period.from && item.to === period.to ? 'small primary' : 'small'
              }
              onClick={() => setPeriod({ from: item.from, to: item.to })}
            >
              {item.label}
            </button>
          ))}
        </div>
        {!valid ? (
          <p className="error">La data iniziale è dopo quella finale.</p>
        ) : summary.isError ? (
          <p className="error">{summary.error.message}</p>
        ) : (
          <p className="notice info small">
            {summary.data
              ? `${summary.data.rows} righe · entrate ${formatMoney(summary.data.incoming)} · uscite ${formatMoney(summary.data.outgoing)}`
              : 'Conteggio…'}
            {note && <span className="block muted">{note}</span>}
          </p>
        )}
        {error && <p className="error">{error}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={downloading}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={!valid || downloading || !summary.data || summary.data.rows === 0}
            onClick={() => void download()}
          >
            {downloading ? 'Attendere…' : 'Scarica CSV'}
          </button>
        </div>
      </div>
    </dialog>
  );
}
